import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function CleanTwoColumnTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="grid min-h-full grid-cols-[32%_68%] bg-white text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <aside className="bg-slate-50 px-6 py-8 border-r border-slate-200">
        {s.photo && <img src={s.photo} alt="" className="w-24 h-24 rounded-full object-cover mb-5 border-2 border-white shadow-sm" />}
        <h1 className="font-bold text-slate-900 leading-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.5 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>

        <section className="mt-6">
          <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-3 pb-1 border-b border-slate-300" style={{ fontSize: `${0.75 * hScale}rem` }}>Contact</h2>
          <div className="space-y-1.5 text-slate-700" style={{ fontSize: `${0.8 * bScale}rem` }}>
            {personal.email && <p>{personal.email}</p>}
            {personal.phone && <p>{personal.phone}</p>}
            {personal.location && <p>{personal.location}</p>}
            {personal.website && <p className="break-words">{personal.website}</p>}
            {personal.linkedin && <p className="break-words">{personal.linkedin}</p>}
          </div>
        </section>

        {skills.length > 0 && (
          <section className="mt-6">
            <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-3 pb-1 border-b border-slate-300" style={{ fontSize: `${0.75 * hScale}rem` }}>Skills</h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded bg-white border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700" style={{ fontSize: `${0.7 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section className="mt-6">
            <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-3 pb-1 border-b border-slate-300" style={{ fontSize: `${0.75 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <article key={edu.id}>
                  <h3 className="font-semibold text-slate-900" style={{ fontSize: `${0.8 * hScale}rem` }}>{edu.school}</h3>
                  <p className="text-slate-600" style={{ fontSize: `${0.75 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  <p className="text-slate-500" style={{ fontSize: `${0.7 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                  </p>
                </article>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section className="mt-6">
            <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-3 pb-1 border-b border-slate-300" style={{ fontSize: `${0.75 * hScale}rem` }}>Certifications</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <article key={cert.id}>
                  <h3 className="font-semibold text-slate-900" style={{ fontSize: `${0.8 * hScale}rem` }}>{cert.name}</h3>
                  <p className="text-slate-500" style={{ fontSize: `${0.7 * bScale}rem` }}>{cert.issuer}{cert.date && ` • ${cert.date}`}</p>
                </article>
              ))}
            </div>
          </section>
        )}
      </aside>

      <main style={{ padding: pagePadding(s.margins) }}>
        {personal.summary && (
          <section className="border-b border-slate-200 pb-5">
            <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-2" style={{ fontSize: `${0.75 * hScale}rem` }}>Profile</h2>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.9 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section className="mt-5">
            <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-4 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Experience</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <article key={exp.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-500" style={{ fontSize: `${0.8 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700 font-medium" style={{ fontSize: `${0.85 * bScale}rem` }}>{exp.company}{exp.location && ` / ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </article>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section className="mt-6">
            <h2 className="font-bold uppercase tracking-wider text-slate-900 mb-4 pb-1 border-b-2 border-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>Projects</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <article key={proj.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{proj.name}</h3>
                    {proj.url && <span className="text-slate-500 break-all" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.url}</span>}
                  </div>
                  <p className="text-slate-600" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && <p className="text-slate-500 mt-1" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.technologies.join(' / ')}</p>}
                </article>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
